import React from "react";
import { BasicTable } from "../types";
import { FaChair, FaChevronRight, FaHandPointer } from "react-icons/fa";

type Props = {
  table: BasicTable;
  onOpen: (table: BasicTable) => void;
};

const TableCard: React.FC<Props> = ({ table, onOpen }) => {
  const { isAvailable, tableLabel, seatCapacity, name } = table;
  const rawLabel = tableLabel || name || "Table";
  const displayLabel = rawLabel.length > 50 ? `${rawLabel.slice(0, 50).trim()}....` : rawLabel;
  const labelSizeClass =
    rawLabel.length > 40
      ? "text-[0.68rem] leading-[0.95rem] sm:text-[0.78rem] sm:leading-[1.05rem]"
      : rawLabel.length > 28
        ? "text-[0.76rem] leading-[1rem] sm:text-[0.9rem] sm:leading-[1.1rem]"
      : rawLabel.length > 18
          ? "text-[0.88rem] leading-[1.05rem] sm:text-[1rem] sm:leading-[1.15rem]"
          : "text-[0.98rem] leading-[1.1rem] sm:text-lg sm:leading-tight";

  return (
    <button
      type="button"
      onClick={() => onOpen(table)}
      className={`group relative flex aspect-[1/1.14] w-full flex-col overflow-hidden rounded-[1.35rem] border text-left transition-all duration-200 active:scale-[0.97] sm:aspect-square
        ${
          isAvailable
            ? "border-emerald-200/80 bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.8),_rgba(236,253,245,0.95)_45%,_rgba(209,250,229,0.95))] shadow-[0_10px_30px_rgba(16,185,129,0.15)] dark:border-emerald-500/20 dark:bg-[radial-gradient(circle_at_top,_rgba(52,211,153,0.12),_rgba(15,23,42,0.95)_40%,_rgba(2,6,23,0.98))] dark:shadow-[0_18px_42px_rgba(0,0,0,0.35)]"
            : "border-orange-200/90 bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.75),_rgba(255,247,237,0.98)_42%,_rgba(254,215,170,0.95))] shadow-[0_14px_34px_rgba(249,115,22,0.18)] dark:border-orange-500/25 dark:bg-[radial-gradient(circle_at_top,_rgba(251,146,60,0.14),_rgba(30,41,59,0.95)_42%,_rgba(2,6,23,0.98))] dark:shadow-[0_18px_42px_rgba(0,0,0,0.4)]"
        }`}
    >
      <div
        className={`absolute inset-x-3 top-3 h-16 rounded-full blur-2xl transition-opacity duration-200 ${
          isAvailable ? "bg-emerald-300/40 opacity-80 dark:bg-emerald-400/20" : "bg-orange-300/50 opacity-90 dark:bg-orange-400/20"
        }`}
      />

      <div className="relative flex h-full flex-col p-2.5 sm:p-3.5">
        <div className="flex items-start justify-between gap-2">
          <span
            className={`inline-flex items-center gap-1 rounded-full px-1.5 py-1 text-[9px] font-semibold uppercase tracking-[0.16em] sm:px-2 sm:text-[10px] ${
              isAvailable
                ? "bg-white/80 text-emerald-700 ring-1 ring-emerald-200 dark:bg-slate-900/70 dark:text-emerald-300 dark:ring-emerald-500/20"
                : "bg-white/80 text-orange-700 ring-1 ring-orange-200 dark:bg-slate-900/70 dark:text-orange-300 dark:ring-orange-500/20"
            }`}
          >
            <span
              className={`h-1.5 w-1.5 rounded-full ${
                isAvailable ? "bg-emerald-500" : "bg-orange-500"
              }`}
            />
            {isAvailable ? "Ready" : "Dining"}
          </span>

          <div
            className={`flex h-7 w-7 items-center justify-center rounded-full text-[11px] sm:h-8 sm:w-8 sm:text-xs ${
              isAvailable
                ? "bg-emerald-600/10 text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-300"
                : "bg-orange-600/10 text-orange-700 dark:bg-orange-400/10 dark:text-orange-300"
            }`}
          >
            <FaChevronRight className="transition-transform duration-200 group-hover:translate-x-0.5" />
          </div>
        </div>

        <div className="mt-2 flex min-h-0 flex-1 flex-col justify-center">
          <h3
            className={`font-black break-words [display:-webkit-box] [-webkit-box-orient:vertical] overflow-hidden [-webkit-line-clamp:3] ${labelSizeClass} ${
              isAvailable ? "text-slate-900 dark:text-slate-100" : "text-slate-950 dark:text-slate-50"
            }`}
            title={rawLabel}
          >
            {displayLabel}
          </h3>
        </div>

        <div className="mt-2 min-w-0 space-y-1.5">
          <div className="flex items-center justify-start gap-2">
            {seatCapacity && (
              <div className="inline-flex max-w-full items-center gap-1 rounded-full bg-slate-950/5 px-2 py-1 text-[10px] font-semibold text-slate-700 dark:bg-white/8 dark:text-slate-200 sm:px-2.5 sm:text-[11px]">
                <FaChair className="text-[11px]" />
                <span className="truncate">{seatCapacity} seats</span>
              </div>
            )}
          </div>
          <div className="inline-flex items-center gap-1 text-[10px] leading-4 font-medium text-slate-600 dark:text-slate-400 sm:text-[11px]">
            <FaHandPointer className="shrink-0 text-[10px]" />
            <span>Tap</span>
          </div>
        </div>
      </div>
    </button>
  );
};

export default TableCard;
